import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { constants } from 'config/constants';
import { getDownloadURL } from 'library/firebase/fb.utils';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import useManagePermittedRoles from 'library/hooks/useManagePermittedRoles';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCategories } from 'reduxStore/categories/selectors';
import { devicesActions } from 'reduxStore/devices/slice';
import { selectFloors } from 'reduxStore/floors/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { LeafletMap, Loader } from 'zComponents/atoms';
import EditableDeviceField from 'zComponents/molecules/EditableDeviceField';
import {
	Alert,
	Button,
	Col,
	Divider,
	Empty,
	Flex,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Select,
	Tooltip,
	Typography
} from 'zui/Antd';

const { Title, Text } = Typography;

const AddDeviceModal = ({ children, visibleProp, setVisibleProp }) => {
	const {
		token: {
			paddingXL,
			paddingLG,
			fontSizeSM,
			colorTextDescription,
			fontWeightStrong,
			paddingXS,
			colorSplit,
			colorBorder,
			paddingMD
		}
	} = theme.useToken();

	const dispatch = useDispatch();
	const { createDeviceLoading } = useSelector((state) => state.Devices);
	const sitesData = useSelector(selectSites);
	const floorsData = useSelector(selectFloors);
	const categoriesData = useSelector(selectCategories);

	const canAddDevices = useManagePermittedRoles('addDevice');
	const isGlobalAdmin = useManagePermittedRoles('manageSiteAssignment');

	const [form] = Form.useForm();
	const [internalVisible, setInternalVisible] = useState(false);

	const [floorOptions, setFloorOptions] = useState([]);
	const [selectedFloor, setSelectedFloor] = useState(null);
	const [loading, setLoading] = useState(false);
	const [selectedFields, setSelectedFields] = useState({});
	const [mapID, setMapID] = useState(null);
	const [marker, setMarker] = useState(null);

	const [mapUrl, setMapUrl] = useState('');
	const watchSite = Form.useWatch('site', form);
	const watchFloor = Form.useWatch('floor', form);
	const watchCategory = Form.useWatch('categoryID', form);
	const watchDevicesCount = Form.useWatch('devices', form);

	const filteredFloors = useMemo(() => {
		if (!watchSite || !floorsData) return [];
		return floorsData.filter((floor) => floor?.siteID === watchSite);
	}, [watchSite, floorsData]);

	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;

	const handleCancel = useCallback(() => {
		form.resetFields();
		setSelectedFields({});
		setMarker(null);
		setMapID(null);
		setVisible(false);
	}, [form, setSelectedFields, setVisible]);

	useEffect(() => {
		setFloorOptions(filteredFloors);
		if (filteredFloors.length === 0) {
			form.setFieldValue('floor', undefined);
		}
	}, [filteredFloors, form]);

	useEffect(() => {
		form.setFieldValue('floor', undefined);
		setSelectedFloor(null);
		setMapUrl('');
		setMarker(null);
	}, [watchSite, form]);

	useEffect(() => {
		if (watchCategory) {
			const currentFields = form.getFieldValue('fields');
			if (currentFields && currentFields.length > 0) {
				form.setFieldValue('fields', []);
				setSelectedFields({});
			}
		}
	}, [watchCategory, form]);

	const currentFloor = useMemo(() => {
		if (!watchFloor || !floorsData) return null;
		return floorsData.find((f) => f.floorID === watchFloor) || null;
	}, [watchFloor, floorsData]);

	useEffect(() => {
		setSelectedFloor(currentFloor);
		setMapID(currentFloor?.mapID || null);

		if (!currentFloor?.url || currentFloor.url === 'N/A') {
			setMapUrl('');
			return;
		}

		// fetch the URL
		let cancelled = false;
		async function fetchMap() {
			setLoading(true);
			try {
				const url = await getDownloadURL(currentFloor.url);
				if (!cancelled) {
					setMapUrl(url);
				}
			} catch {
				if (!cancelled) {
					setMapUrl('');
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		}

		fetchMap();

		return () => {
			cancelled = true;
		};
	}, [currentFloor]);

	const handleOpenModal = useCallback(() => {
		if (canAddDevices) {
			setVisible(true);
		}
	}, [canAddDevices, setVisible]);

	const handleSubmit = useCallback(
		(values) => {
			const { devices: deviceCount, ...restValues } = values;
			const numberOfDevices = deviceCount || 1; // Default to 1 if not specified

			// Use the appropriate action based on device count
			if (numberOfDevices > 1) {
				dispatch(
					devicesActions.createMultipleDevices({
						...values,
						mapID,
						...(marker && { coordinates: { x: marker.lng, y: marker.lat } })
					})
				);
			} else {
				dispatch(
					devicesActions.createDevice({
						...restValues,
						mapID,
						...(marker && { coordinates: { x: marker.lng, y: marker.lat } })
					})
				);
			}

			setTimeout(() => {
				handleCancel();
			}, 500);
		},
		[dispatch, handleCancel, mapID, marker]
	);

	const editLocations = useMemo(() => (marker ? [{ x: marker.lng, y: marker.lat }] : []), [marker]);
	const drawSingleMarker = useMemo(() => !marker, [marker]);

	// Group categories by type for select component, excluding certain categories
	const groupCategories = useMemo(() => {
		if (!categoriesData || categoriesData.length === 0) return [];

		const options = [];
		// Filter out categories that are not allowed to be added
		categoriesData
			.filter((category) => !constants.ADD_DEVICE_EXCLUDED_CATEGORIES.includes(category.defaultCategoryID))
			.forEach((category) => {
				if (options.every((option) => option.typeID !== category.typeID)) {
					options.push({
						label: category.typeName,
						typeID: category.typeID,
						options: []
					});
				}
				options
					.find((option) => option.typeID === category.typeID)
					.options.push({
						label: category.name,
						value: category.categoryID
					});
			});
		return options;
	}, [categoriesData]);

	// Get available fields for the selected category
	const availableFields = useMemo(() => {
		if (!watchCategory || !categoriesData) return [];
		const selectedCategory = categoriesData.find((cat) => cat.categoryID === watchCategory);
		return selectedCategory?.fields || [];
	}, [watchCategory, categoriesData]);

	const handleFieldChange = useCallback(
		(index, key, value) => {
			const currentFields = form.getFieldValue('fields') || [];
			const updated = [...currentFields];

			if (!updated[index]) {
				updated[index] = {};
			}

			updated[index][key] = value;

			if (key === 'fieldID') {
				const categoryField = availableFields.find((field) => field.fieldID === value);
				updated[index].fieldTypeID = categoryField?.fieldTypeID;
				updated[index].value = undefined;
				setSelectedFields((prev) => ({ ...prev, [index]: value }));
			}

			form.setFieldValue('fields', updated);
		},
		[form, availableFields]
	);

	return (
		<>
			<Tooltip title={canAddDevices ? 'Add Device' : "You don't have permission"}>
				{children ? (
					children
				) : (
					<Button type='primary' onClick={handleOpenModal} disabled={!canAddDevices}>
						Add Device
					</Button>
				)}
			</Tooltip>
			<Modal
				open={visible}
				onCancel={handleCancel}
				destroyOnClose
				width={1154}
				closeIcon={<CloseOutlined />}
				centered
				footer={[
					<Button key='cancel' onClick={handleCancel}>
						Cancel
					</Button>,
					<Button
						htmlType='submit'
						type='primary'
						key='submit'
						form={form}
						loading={createDeviceLoading}
						disabled={createDeviceLoading}
					>
						{watchDevicesCount && watchDevicesCount > 1 ? `Add ${watchDevicesCount} Devices` : 'Add Device'}
					</Button>
				]}
				styles={{
					body: {
						height: 650,
						display: 'flex',
						flexDirection: 'column'
					},
					content: { padding: paddingXL, height: '100%' },
					footer: { marginTop: 0 }
				}}
			>
				<Form
					form={form}
					id={form}
					onFinish={handleSubmit}
					layout='vertical'
					style={{ height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}
				>
					<Flex gap={paddingXL} justify='space-between' align='stretch' style={{ height: '100%', flex: 1 }}>
						{/* LEFT COLUMN */}
						<Flex vertical gap={paddingXL} flex={1} style={{ height: '100%' }}>
							<Flex vertical style={{ marginBottom: paddingXS }}>
								<Title level={4} style={{ margin: 0 }}>
									Add New Device
								</Title>
								<Text
									style={{
										fontSize: fontSizeSM,
										color: colorTextDescription,
										lineHeight: '20px'
									}}
								>
									Add a new Device to your site. A Device is an access point that People interact with
									to gain entry, such as a lock or reader.
								</Text>
							</Flex>
							<Form.Item
								name='name'
								label='Device Name'
								style={{ fontWeight: fontWeightStrong, margin: 0 }}
								rules={fieldsValidator.Name}
								required
							>
								<Input placeholder='Enter device name' />
							</Form.Item>

							{/* Helper text for multiple devices */}

							<Row gutter={[16, 24]} style={{ marginBottom: paddingLG }}>
								<Col lg={12} md={24}>
									<Form.Item
										name='categoryID'
										label='Category'
										style={{ fontWeight: fontWeightStrong, margin: 0 }}
										rules={[
											{
												required: true,
												message: 'Please select a Category'
											}
										]}
										required
									>
										<Select
											options={groupCategories}
											placeholder='Select Category'
											style={{ fontWeight: 400 }}
										/>
									</Form.Item>
								</Col>

								<Col lg={12} md={24}>
									<Form.Item
										name='devices'
										label='Number of Devices'
										style={{ fontWeight: fontWeightStrong, margin: 0 }}
										required={false}
										rules={[
											{
												type: 'number',
												min: 1,
												max: 50,
												message: 'Number must be between 1 and 50'
											}
										]}
									>
										<InputNumber
											placeholder='Enter number of devices'
											style={{ width: '100%' }}
											min={1}
											max={50}
										/>
									</Form.Item>
								</Col>
							</Row>
							<Divider style={{ marginBottom: paddingLG }} />

							{/* Additional Information */}
							<Flex vertical flex={1} style={{ minHeight: 0 }}>
								<Flex vertical>
									<Text style={{ margin: 0, fontWeight: fontWeightStrong }}>
										Additional Information
									</Text>
								</Flex>
								{watchCategory ? (
									availableFields.length > 0 ? (
										<Form.List name='fields'>
											{(fields, { add, remove }) => (
												<Flex
													vertical
													gap={paddingMD}
													style={{
														flex: 1,
														minHeight: 0,
														overflowY: 'auto',
														overflowX: 'hidden',
														scrollbarWidth: 'thin',
														paddingRight: 8,
														paddingTop: paddingXS,
														width: '100%'
													}}
												>
													{fields.map(({ key, name, ...restField }) => {
														const currentFields = form.getFieldValue('fields') || [];
														const currentField = currentFields[name] || {};
														const selectedFieldID =
															selectedFields[name] || currentField.fieldID;
														const categoryField = availableFields.find(
															(f) => f.fieldID === selectedFieldID
														);

														const filteredOptions = availableFields
															.filter((f) => {
																const usedFields = currentFields
																	.map((item, i) =>
																		i !== name ? item.fieldID : null
																	)
																	.filter(Boolean);
																return !usedFields.includes(f.fieldID);
															})
															.map((f) => ({ label: f.title, value: f.fieldID }));

														return (
															<Row
																key={key}
																gutter={[12, 16]}
																align='middle'
																style={{ width: '100%', margin: 0 }}
															>
																<Col xs={24} md={11}>
																	<Form.Item
																		{...restField}
																		name={[name, 'fieldID']}
																		rules={[
																			{
																				required: true,
																				message: 'Please select a field'
																			}
																		]}
																		style={{ marginBottom: 0 }}
																	>
																		<Select
																			placeholder='Select Field'
																			options={filteredOptions}
																			onChange={(value) =>
																				handleFieldChange(
																					name,
																					'fieldID',
																					value
																				)
																			}
																		/>
																	</Form.Item>
																</Col>

																<Col xs={24} md={11}>
																	<Form.Item
																		{...restField}
																		name={[name, 'value']}
																		rules={[
																			{
																				required: true,
																				message: 'Please enter a value'
																			}
																		]}
																		style={{ marginBottom: 0 }}
																	>
																		<EditableDeviceField
																			fieldTypeID={categoryField?.fieldTypeID}
																			value={currentField.value}
																			onChange={(value) =>
																				handleFieldChange(name, 'value', value)
																			}
																			onChangeInput={(value) =>
																				handleFieldChange(name, 'value', value)
																			}
																			disabled={createDeviceLoading}
																		/>
																	</Form.Item>
																</Col>

																<Col xs={24} md={2}>
																	<Tooltip title='Remove Field'>
																		<Button
																			type='text'
																			icon={<CloseOutlined />}
																			style={{ color: colorTextDescription }}
																			onClick={() => remove(name)}
																			disabled={createDeviceLoading}
																		/>
																	</Tooltip>
																</Col>
															</Row>
														);
													})}

													{fields.length < availableFields.length && (
														<Form.Item>
															<Button
																type='dashed'
																onClick={() =>
																	add({ fieldID: undefined, value: undefined })
																}
																block
																icon={<PlusOutlined />}
																disabled={createDeviceLoading}
															>
																Add Custom Field
															</Button>
														</Form.Item>
													)}
												</Flex>
											)}
										</Form.List>
									) : (
										<Empty
											description='No custom fields available'
											image={Empty.PRESENTED_IMAGE_SIMPLE}
										/>
									)
								) : (
									<Empty
										description='Select a category to add additional information'
										image={Empty.PRESENTED_IMAGE_SIMPLE}
									/>
								)}
							</Flex>
						</Flex>

						{/* Vertical Divider */}
						<div
							style={{
								width: 1,
								backgroundColor: colorSplit,
								margin: `0 ${paddingXS}px 0px`
							}}
						/>

						{/* RIGHT COLUMN */}
						<Flex vertical gap={paddingXL} flex={1}>
							<Row gutter={[16, 24]} style={{ marginTop: paddingMD }}>
								<Col lg={12} md={24}>
									<Form.Item
										name='site'
										label='Site'
										style={{ fontWeight: fontWeightStrong, margin: 0 }}
										required={false}
									>
										<Select
											options={sitesData?.map((site) => ({
												label: site.name,
												value: site.siteID
											}))}
											placeholder='Select'
											allowClear={isGlobalAdmin}
											style={{ fontWeight: 400 }}
										/>
									</Form.Item>
								</Col>

								<Col lg={12} md={24}>
									<Form.Item
										name='floor'
										label='Floor'
										style={{ fontWeight: fontWeightStrong, margin: 0 }}
										required={false}
									>
										<Select
											disabled={!watchSite}
											options={floorOptions?.map((floor) => ({
												label: floor.name,
												value: floor.floorID
											}))}
											placeholder='Select'
											style={{ fontWeight: 400 }}
										/>
									</Form.Item>
								</Col>
							</Row>

							{selectedFloor && (
								<Flex vertical gap={paddingXS}>
									<Text style={{ fontWeight: fontWeightStrong, fontSize: 16, marginBottom: 8 }}>
										Position Device on Floorplan
									</Text>

									{mapUrl && (
										<Alert
											type='info'
											showIcon
											style={{ marginBottom: paddingXS }}
											message='Click the icon on the top right of the map to place the device location.'
										/>
									)}
									{loading ? (
										<Loader />
									) : (
										<div
											style={{
												height: 260,
												position: 'relative',
												border: `1px dashed ${colorBorder}`,
												borderRadius: 6,
												overflow: 'hidden',

												padding: '12px 8px',
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center'
											}}
										>
											{mapUrl ? (
												<LeafletMap
													mapHeight={260}
													mapWidth={600}
													scrollWheelZoom={true}
													url={mapUrl}
													setMarker={setMarker}
													editMarkerLocation={editLocations}
													// draggableMarkerLocation={draggableMarker}
													drawSingleMarkerEnabled={drawSingleMarker}
													expandMapControl={false}
												/>
											) : (
												<Empty description='Please upload a floor plan from the floors page' />
											)}
										</div>
									)}
								</Flex>
							)}
						</Flex>
					</Flex>
				</Form>
			</Modal>
		</>
	);
};

export default AddDeviceModal;
